<template>
  <div class="aed">
    <!-- Identity -->
    <div class="aed__section">
      <div class="aed__head">
        <i class="fa-solid fa-building"></i><span>{{ $t('affiliates.identitySection') }}</span>
      </div>
      <div class="aed__grid">
        <div class="aed__field">
          <label>{{ $t('columns.company') }}</label>
          <!-- New affiliate created for a desk: the desk name is shown in
               front and saved as the start of the name ("Desk 1 test") -->
          <b-input-group v-if="newAffiliate && namePrefix">
            <b-input-group-prepend is-text class="aed__prefix">{{ namePrefix }}</b-input-group-prepend>
            <b-form-input v-model="affiliate.company"/>
          </b-input-group>
          <b-form-input v-else v-model="affiliate.company"/>
          <small v-if="newAffiliate" class="aed__hint">{{ $t('affiliates.companyHint') }}</small>
        </div>
        <div class="aed__field">
          <label>{{ $t('columns.alias') }}</label>
          <b-form-input v-model="affiliate.alias"/>
        </div>
      </div>
    </div>

    <!-- Contact -->
    <div class="aed__section">
      <div class="aed__head">
        <i class="fa-regular fa-address-card"></i><span>{{ $t('affiliates.contactSection') }}</span>
      </div>
      <div class="aed__grid">
        <div class="aed__field">
          <label>{{ $t('columns.email') }}</label>
          <b-form-input v-model="affiliate.email" type="email"/>
        </div>
        <div class="aed__field">
          <label>{{ $t('columns.skype') }}</label>
          <b-form-input v-model="affiliate.skype"/>
        </div>
      </div>
    </div>

    <!-- Settings -->
    <div class="aed__section">
      <div class="aed__head">
        <i class="fa-solid fa-sliders"></i><span>{{ $t('affiliates.settingsSection') }}</span>
      </div>
      <div class="aed__grid">
        <div class="aed__field">
          <label>{{ $t('defaultClientStatus') }}</label>
          <client-status-picker v-model="affiliate.clientStatusId" auto-init/>
        </div>
        <div class="aed__field aed__field--switch">
          <b-checkbox switch v-model="affiliate.unforceClientDuplicate">
            {{ $t('columns.unforceClientDuplicate') }}
          </b-checkbox>
        </div>
        <!-- External API only: fake-deposit clients are listed as FTDs to the affiliate -->
        <div class="aed__field">
          <b-checkbox switch v-model="affiliate.showFakeDeposits"
                      :disabled="!hasPermission(permissions.AFFILIATE_UPDATE)">
            {{ $t('affiliates.showFakeDeposits') }}
          </b-checkbox>
          <small class="aed__hint">{{ $t('affiliates.showFakeDepositsHint') }}</small>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="aed__footer">
      <b-button variant="primary" @click="save">
        <template v-if="newAffiliate">
          {{ $t('affiliates.createContinue') }}<i class="fa-solid fa-arrow-right ml-50"></i>
        </template>
        <template v-else>
          <i class="fa-solid fa-floppy-disk mr-50"></i>{{ $t('operations.save') }}
        </template>
      </b-button>
    </div>

    <email-editor-modal @emailTemplateSaved="sendEmail" ref="emailEditorModal" :receiver="affiliate"/>
  </div>
</template>

<script>
import Affiliate from '@/model/affiliate/Affiliate';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import { mapActions } from 'vuex';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinBase from '@/components/mixin/mixinBase';
import EmailEditorModal from '@/components/partial/mailTemplates/EmailEditorModal';
import ClientStatusPicker from '@/components/widget/picker/clientStatusPicker.vue'

export default {
  name: 'AffiliateEditDetails',
  components: {
    ClientStatusPicker,
    EmailEditorModal,
  },
  mixins: [mixinPermissions, mixinNotifications, mixinBase],
  data: () => ({
    affiliate: Affiliate(),
    tokens: [],
  }),
  props: {
    value: {
      required: true,
      default: () => ({})
    },
    newAffiliate: {
      type: Boolean,
      default: () => false
    },
    // New-affiliate wizard: the picked desk, put in front of the company name
    namePrefix: {
      type: String,
      default: null
    }
  },
  watch: {
    value: {
      handler() {
        this.affiliate = Object.assign(Affiliate(), this.value);
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions('affiliate', ['getAffiliateTokens', 'updateAffiliate', 'createAffiliate']),
    openEmailEditor() {
      this.$refs.emailEditorModal.showModal = true;
    },
    sendEmail() {
      this.$refs.emailEditorModal.showModal = false;
    },
    updateAffiliateTokens() {
      this.tokens = [];
      this.getAffiliateTokens({ id: this.affiliate.id })
          .then(response => {
            this.tokens = response.data;
          });
    },
    // "<desk> <name>" — not doubled when the user already typed the desk himself
    companyWithPrefix() {
      const name = (this.affiliate.company || '').trim();
      const prefix = (this.namePrefix || '').trim();
      if (!prefix) return this.affiliate.company;
      if (name.toLowerCase().startsWith(prefix.toLowerCase())) return name;
      return `${prefix} ${name}`.trim();
    },
    async addAffiliate() {
      let $this = this;
      try {
        let affiliate = await this.createAffiliate({
          affiliate: { ...this.affiliate, company: this.companyWithPrefix() },
        });
        this.showSuccessNotification($this.$t('globalMessages.addDataSuccess', { system: $this.$t('menu.affiliates') }));
        this.$emit('input', affiliate);
        this.$emit('updated', affiliate.id);
        this.$emit('submitted', affiliate);
        this.refreshTables();
      } catch (err) {
        $this.showServerErrorNotification('addDataError', 'affiliates', err);
      }
    },
    async modifyAffiliate() {
      let $this = this;
      try {
        let affiliate = await this.updateAffiliate({
          affiliate: this.affiliate,
          id: this.affiliate.id
        });
        this.showSuccessNotification($this.$t('globalMessages.updateDataSuccess', { system: $this.$t('menu.affiliates') }));
        this.$emit('input', affiliate);
        this.$emit('updated', affiliate.id);
        this.refreshTables();
      } catch (err) {
        $this.showServerErrorNotification('addDataError', 'affiliates', err);
      }
    },
    async save() {
      if (this.affiliate.id == null) {
        await this.addAffiliate();
      } else {
        await this.modifyAffiliate();
      }
    }
  }
};
</script>

<style scoped>
/* Tidy sectioned form — inherits the affiliate modal's --am-* palette. */
.aed__section {
  margin-bottom: 1.1rem;
}
.aed__head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.73rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--am-text-muted, #94a3b8);
  margin-bottom: 0.6rem;
  padding-bottom: 0.35rem;
  border-bottom: 1px dashed var(--am-border, #e2e8f0);
}
.aed__head i {
  color: var(--am-primary, #2563eb);
  font-size: 0.85rem;
}
.aed__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.8rem 1.1rem;
}
.aed__field label {
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--am-text-2, #475569);
  margin-bottom: 0.3rem;
}
.aed__field--switch {
  display: flex;
  align-items: center;
  padding-top: 1.1rem; /* aligns the switch with the neighbour's input row */
}
.aed__prefix ::v-deep .input-group-text {
  font-weight: 700;
  color: var(--am-primary, #007bff);
}
.aed__hint {
  display: block;
  margin-top: 0.25rem;
  color: var(--am-text-muted, #94a3b8);
  font-size: 0.72rem;
}
.aed__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.3rem;
  padding-top: 0.9rem;
  border-top: 1px solid var(--am-border, #e2e8f0);
}

@media (max-width: 575px) {
  .aed__grid { grid-template-columns: 1fr; }
}
</style>
